/**
 * NuovaPraticaPage — F-003.2 Phase 4 (IMPL-P4-001) + F-005 Phase 5 (IMPL-P5-003).
 *
 * Container per /app/pratiche/nuova.
 *
 * Mode routing (URL ?mode=):
 *  - Legge ?mode URL param (wizard | dense)
 *  - Se assente, redirige in base a `SessionUser.tipologia`:
 *      AGENTE / COMMERCIALISTA → ?mode=dense
 *      PRIVATO / null         → ?mode=wizard  ← Q2: null default = wizard
 *  - Usa setSearchParams({ mode }, { replace: true }) per evitare voce
 *    inutile in history
 *
 * Correction routing (URL ?correctMode=1&id=:praticaId — F-005):
 *  - Quando `correctMode=1` la pagina NON crea una nuova BOZZA: usa la
 *    pratica `id` (in stato BLOCCATA) come pratica "in correzione".
 *  - Fa fetch di `GET /api/v1/pratiche/:id/detail`, deriva `errorFields`
 *    da `pratica.blockedReasons` via `expandBlockedReasonsToFields`, e
 *    idrata il form via `useContractForm({ initialData })`.
 *  - Il wizard è aperto sul primo step contenente almeno un campo
 *    contestato (computato in base a `STEP_FIELDS`). Se nessun campo
 *    mappato è in `errorFields`, parte da step 0.
 *  - In correctMode `<ErrorFieldsProvider>` propaga la lista contestata
 *    ai field component (amber ring + sr-only hint).
 *
 * BOZZA creation (mode='create' — default):
 *  - Q1: Crea sempre una nuova BOZZA al mount — non cerca BOZZE esistenti.
 *    "Riprendi bozza" è deferred a F-005 o successore.
 *  - Il praticaId viene preservato in state locale; passato a ContractWizard.
 *
 * Cross-feature imports (accettabili per riuso componenti UI):
 *  - ProfileIncompleteBanner da features/profilo/components/
 *  - useBillingProfile da features/profilo/hooks/
 *  - fetchPraticaDetail / expandBlockedReasonsToFields da features/pratiche-cliente/
 *
 * Architettura interna:
 *  - `NuovaPraticaInner` legge i searchParam e dispatcha al sotto-componente
 *    appropriato (`CreateFlow` o `CorrectModeFlow`). Splittare i due flussi
 *    in componenti distinti garantisce che gli hook (useQuery, useEffect,
 *    useMemo) di un flusso non vengano invocati nell'altro — tipico React
 *    "rules of hooks" + isolamento dei test.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useSearchParams, useParams, useNavigate, Link } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Send } from 'lucide-react';
import { RequireAuth } from '@/features/auth/components/RequireAuth';
import { AppLayout } from '@/components/layout/AppLayout';
import { useAuthSession } from '@/features/auth/hooks/useAuthSession';
import { useBillingProfile } from '@/features/profilo/hooks/useBillingProfile';
import { ProfileIncompleteBanner } from '@/features/profilo/components/ProfileIncompleteBanner';
import { ContractWizard } from '../components/wizard/ContractWizard';
import { ContractDenseForm } from '../components/dense';
import {
  createBozza,
  isPraticaApiError,
  type PraticaView,
} from '../lib/pratica-client';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ErrorFieldsProvider } from '../hooks/useErrorFields';
import { usePraticaStatus } from '@/features/pratica-status/hooks/usePraticaStatus';
import { usePraticaCorreggi } from '@/features/pratica-status/hooks/usePraticaCorreggi';
import { AdempimentoInner } from '@/features/adempimento/pages/AdempimentoInner';

export function NuovaPraticaPage() {
  return (
    <RequireAuth>
      <AppLayout>
        <NuovaPraticaInner />
      </AppLayout>
    </RequireAuth>
  );
}

/**
 * Dispatcher: routa al CreateFlow (default), CorrectModeFlow (F-005) o
 * AdempimentoInner (F-004). Non chiama hook che dipendono dal flusso (es.
 * useQuery per fetchPraticaDetail) — così rimane economico e i flussi
 * mantengono "rules of hooks" puri.
 */
function NuovaPraticaInner() {
  // Correction mode detection (F-005 Phase 6): route
  // `/pratiche/:correctionOfId/correggi` re-uses this page in correction mode.
  // When the param exists we render the dedicated correction form and skip the
  // BOZZA-creation / wizard code path entirely (existing behavior untouched).
  const { correctionOfId } = useParams<{ correctionOfId?: string }>();
  const [searchParams] = useSearchParams();
  if (correctionOfId) {
    return <CorrezionePraticaInner correctionOfId={correctionOfId} />;
  }
  // F-004 (IMPL-P4-002): adempimento mode via `?adempimentoMode=1&parentId=…`.
  // Riuso della route `/app/pratiche/nuova` (Pattern 3) — nessuna nuova
  // `<Route>` in `App.tsx`. Branch sibling della correction mode.
  if (searchParams.get('adempimentoMode') === '1') {
    return <AdempimentoInner parentId={searchParams.get('parentId') ?? undefined} />;
  }
  return <NuovaPraticaWizardInner />;
}

/**
 * Flusso di creazione di una nuova pratica (BOZZA → INVIATA), F-003.x.
 * Non viene mai montato in correction mode.
 */
function NuovaPraticaWizardInner() {
  const { user } = useAuthSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const mode = searchParams.get('mode');
  const navigate = useNavigate();

  // Mode routing: redirect in base a `tipologia` se ?mode assente
  useEffect(() => {
    if (mode) return; // già impostato, non fare nulla
    const target =
      user?.tipologia === 'AGENTE' || user?.tipologia === 'COMMERCIALISTA'
        ? 'dense'
        : 'wizard'; // PRIVATO o null → wizard (Q2)
    setSearchParams({ mode: target }, { replace: true });
  }, [mode, user?.tipologia, setSearchParams]);

  // BOZZA creation on mount (Q1: sempre nuova)
  const [praticaId, setPraticaId] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const p = await createBozza();
        if (!cancelled) setPraticaId(p.id);
      } catch (err) {
        if (!cancelled) {
          setCreateError(
            isPraticaApiError(err) ? err.message : 'Errore durante la creazione della bozza.',
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentional: empty deps — solo al mount

  // Billing profile guard
  const billingProfile = useBillingProfile();

  const handleSubmitted = (pratica: PraticaView) => {
    navigate(`/app/pratiche/${pratica.id}`);
  };

  return (
    <section className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-3xl font-semibold tracking-tight">Nuova pratica</h1>
        <p className="text-muted-foreground">
          Compila i dati del contratto di locazione passo per passo.
        </p>
      </header>

      {/* Banner profilo incompleto (riusa F-002 component) */}
      {billingProfile.status !== 'present' && billingProfile.status !== 'loading' && (
        <ProfileIncompleteBanner />
      )}

      {/* Errore creazione bozza */}
      {createError && (
        <Alert variant="destructive" role="alert">
          {createError}
        </Alert>
      )}

      {/* Loading state (BOZZA non ancora creata) */}
      {!praticaId && !createError && (
        <p className="text-muted-foreground text-sm" aria-live="polite">
          Preparazione pratica…
        </p>
      )}

      {/* Wizard (mode=wizard + praticaId disponibile) */}
      {praticaId && mode === 'wizard' && (
        <ErrorFieldsProvider value={[]}>
          <ContractWizard
            praticaId={praticaId}
            onSubmitted={handleSubmitted}
            paymentMode={user?.tipologia === 'PRIVATO'}
          />
        </ErrorFieldsProvider>
      )}

      {/* Form denso (mode=dense + praticaId disponibile) — F-003.3 + F-003.4 */}
      {praticaId && mode === 'dense' && (
        <ContractDenseForm praticaId={praticaId} onSubmitted={handleSubmitted} />
      )}
    </section>
  );
}

/**
 * CorrezionePraticaInner — F-005 Phase 6 (correctionOf mode).
 *
 * Form di correzione self-service per pratiche BLOCCATA. Riusa lo stesso punto
 * di montaggio di NuovaPraticaPage (route `/pratiche/:correctionOfId/correggi`)
 * ma non crea una nuova BOZZA: legge lo stato della pratica originale via
 * `usePraticaStatus`, evidenzia i campi in `contestedFields` (amber ring),
 * mostra la nota operatore + il nuovo checkbox di consenso, e ri-sottomette
 * via PATCH `/api/v1/pratiche/:id/correggi` (BLOCCATA → INVIATA).
 *
 * Nota scope (deferred): l'upload del documento sostitutivo e il prefill
 * completo del wizard F-003 sono fuori scope V1. `PraticaStatusDetail` non
 * espone `datiStrutturati`, quindi il body PATCH invia solo i campi contestati
 * corretti raccolti in questo form. Vedi IMPLEMENTATION_REPORT_PHASE_6.json.
 */
function CorrezionePraticaInner({ correctionOfId }: { correctionOfId: string }) {
  const navigate = useNavigate();
  const { data: praticaOriginale, isLoading, isError } = usePraticaStatus(correctionOfId);
  const correggiMutation = usePraticaCorreggi(correctionOfId);

  const [consenso, setConsenso] = useState(false);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const contestedFields = praticaOriginale?.contestedFields ?? [];

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitError(null);
    if (!consenso) return;
    try {
      await correggiMutation.mutateAsync({
        consenso: true,
        datiStrutturati: fieldValues,
      });
      navigate(`/pratiche/${correctionOfId}/stato`);
    } catch {
      setSubmitError('Errore durante la ri-sottomissione. Riprova.');
    }
  }

  if (isLoading) {
    return (
      <p className="text-muted-foreground text-sm" aria-live="polite">
        Caricamento pratica…
      </p>
    );
  }

  if (isError || !praticaOriginale) {
    return (
      <Alert variant="destructive" role="alert">
        Impossibile caricare i dati della pratica da correggere.
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <Button
          variant="link"
          size="sm"
          asChild
          className="h-auto px-0 text-muted-foreground"
        >
          <Link to={`/pratiche/${correctionOfId}/stato`}>
            <ArrowLeft className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
            Dettaglio pratica
          </Link>
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">Correggi la pratica</h1>
      </header>

      <Alert className="border-amber-300 bg-amber-50 text-amber-900">
        <AlertTriangle className="h-4 w-4 text-amber-600" aria-hidden="true" />
        <AlertTitle className="text-amber-900">Campi da correggere</AlertTitle>
        <AlertDescription className="text-amber-800">
          {praticaOriginale.operatorNoteVisible ??
            'I campi evidenziati in arancione richiedono correzione. Gli altri campi sono già compilati correttamente.'}
        </AlertDescription>
      </Alert>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Dati da correggere</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {contestedFields.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Nessun campo specifico segnalato. Verifica la nota dell'operatore e
                conferma per ri-sottomettere.
              </p>
            ) : (
              contestedFields.map((path) => (
                <div
                  key={path}
                  className="space-y-1.5 rounded-md border-l-2 border-amber-400 bg-amber-50/50 py-2 pl-3 ring-2 ring-amber-400"
                >
                  <Label htmlFor={`field-${path}`} className="text-sm font-medium">
                    {path}
                    <span className="ml-2 text-xs font-normal text-amber-700">
                      (da correggere)
                    </span>
                  </Label>
                  <Input
                    id={`field-${path}`}
                    value={fieldValues[path] ?? ''}
                    onChange={(e) =>
                      setFieldValues((prev) => ({ ...prev, [path]: e.target.value }))
                    }
                    aria-describedby={`field-${path}-hint`}
                    className="border-amber-300 focus-visible:ring-amber-400"
                  />
                  <p
                    className="text-xs text-amber-700"
                    id={`field-${path}-hint`}
                    aria-live="polite"
                  >
                    ⚠ Questo campo è stato segnalato come da correggere.
                  </p>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* New consent checkbox */}
        <div className="flex items-start gap-3 rounded-md border bg-muted/20 p-4">
          <Checkbox
            id="nuovo-consenso"
            checked={consenso}
            onCheckedChange={(value) => setConsenso(value === true)}
          />
          <Label
            htmlFor="nuovo-consenso"
            className="cursor-pointer text-sm leading-relaxed"
          >
            Confermo che i dati corretti sono veritieri e completi, e autorizzo la
            registrazione del contratto con le modifiche apportate.
            <span className="mt-1 block text-xs text-muted-foreground">
              Un nuovo consenso è richiesto per la ri-sottomissione dopo correzione.
            </span>
          </Label>
        </div>

        {submitError && (
          <Alert variant="destructive" role="alert">
            {submitError}
          </Alert>
        )}

        <div className="flex gap-3">
          <Button asChild variant="outline">
            <Link to={`/pratiche/${correctionOfId}/stato`}>Annulla</Link>
          </Button>
          <Button type="submit" disabled={!consenso || correggiMutation.isPending}>
            Ri-sottometti pratica
            <Send className="ml-2 h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </form>
    </div>
  );
}
